/**
 * My account → support conversations.
 *
 * Signed-in view of the customer's own tickets: no bearer token involved at
 * all (the RPCs scope on auth.uid()). Also offers to CLAIM any ticket opened
 * anonymously with the same email address, which rotates the bearer token so
 * the old emailed link stops mattering.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquare, ArrowLeft, Headset } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ThreadTranscript from "@/components/support/ThreadTranscript";
import {
  claimTicket,
  getMyThread,
  listClaimableTickets,
  listMyTickets,
  replyOwn,
  THREAD_STATUS_LABEL,
  type ClaimableTicket,
  type MyTicketRow,
  type Thread,
} from "@/services/support";

function when(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function MySupportTickets() {
  const [rows, setRows] = useState<MyTicketRow[]>([]);
  const [claimable, setClaimable] = useState<ClaimableTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState<Thread | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [mine, claims] = await Promise.all([listMyTickets(), listClaimableTickets()]);
    setRows(mine.data ?? []);
    setClaimable(claims.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function open(id: string) {
    setBusy(true);
    const { data, error } = await getMyThread(id);
    setBusy(false);
    if (error || !data) {
      toast.error("We couldn't open that conversation.");
      return;
    }
    setThread(data);
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const body = reply.trim();
    if (!thread || !body || busy) return;
    setBusy(true);
    const { error } = await replyOwn(thread.id, body);
    setBusy(false);
    if (error) {
      toast.error("We couldn't send that reply. Please try again.");
      return;
    }
    setReply("");
    await open(thread.id);
    await load();
  }

  async function claim(id: string) {
    setBusy(true);
    const { error } = await claimTicket(id);
    setBusy(false);
    if (error) {
      toast.error("We couldn't move that conversation onto your account.");
      return;
    }
    toast.success("Moved onto your account. Any old private link stops working.");
    await load();
  }

  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading your conversations…
      </p>
    );
  }

  if (thread) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setThread(null)}
          className="flex items-center gap-1.5 text-sm font-medium text-primary"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> All conversations
        </button>
        <div>
          <h3 className="text-base font-semibold">{thread.subject}</h3>
          <p className="text-xs text-muted-foreground">
            {THREAD_STATUS_LABEL[thread.status]} · opened {when(thread.created_at)}
          </p>
        </div>
        <ThreadTranscript messages={thread.messages} />
        {thread.status !== "closed" && (
          <form onSubmit={send} className="space-y-2">
            <Textarea
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Add to this conversation…"
            />
            <Button type="submit" size="sm" disabled={busy || !reply.trim()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reply"}
            </Button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {claimable.length > 0 && (
        <div className="space-y-2 rounded-xl border border-primary/40 bg-primary/5 p-3">
          <p className="text-sm font-semibold">Conversations opened with your email address</p>
          <p className="text-xs text-muted-foreground">
            You started these before signing in. Move them onto your account and the private link
            we emailed stops working.
          </p>
          <ul className="space-y-2">
            {claimable.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-sm">{c.subject}</span>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => claim(c.id)}>
                  Claim
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No support conversations yet. Use the Help button to start one.
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => open(r.id)}
                className="flex w-full items-center gap-3 px-3 py-3 text-left transition hover:bg-muted/40"
              >
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{r.subject}</span>
                  <span className="block text-xs text-muted-foreground">
                    {THREAD_STATUS_LABEL[r.status]} · {r.message_count} message
                    {r.message_count === 1 ? "" : "s"} · {when(r.updated_at)}
                  </span>
                </span>
                {r.first_agent_response_at && (
                  <span className="flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                    <Headset className="h-3 w-3" aria-hidden /> Answered
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
